import gql from 'graphql-tag';
import React, {useCallback, useMemo} from 'react';

import {SaveToResultsStepDataName} from 'analysis-tool/AnalysisStepDataNames';
import {updateResultSchema} from 'analysis-tool/structured-data-mapping/DataMappingStateReducers';
import {
  DataFrameColumnToFieldMapping,
  TargetSpecificMappingState,
} from 'analysis-tool/structured-data-mapping/DataMappingTypes';
import SaveToResultsStepper from 'analysis-tool/structured-data-mapping/SaveToResultsStepper';
import {DataFrameColumnDef} from 'data-frames/DataFrameTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLToREST} from 'graphql-client/declarative-translations/external/translate';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  GetResultSchemaQuery,
  GetResultSchemaQueryVariables,
} from 'pipelines/generalized-step-config-editor/editors/custom-editors/__generated/GetResultSchemaQuery';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {
  getDataFrameInfoOrEmptyInfo,
  useStepConfigEditorContext,
} from 'pipelines/generalized-step-config-editor/StepConfigEditorContext';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {JSONValue} from 'util/Types';

const GET_RESULT_SCHEMA_QUERY = gql`
  query GetResultSchemaQuery($schemaId: StrictString!) @skipAudit {
    resultSchema(id: $schemaId) {
      ... on ResultSchemaNode {
        ...ResultSchemaGraphQL
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ResultSchema').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function useInputDataFrameColumns(): Array<DataFrameColumnDef> {
  const {dataFramesInfos} = useStepConfigEditorContext();
  const info = getDataFrameInfoOrEmptyInfo(dataFramesInfos, SaveToResultsStepDataName.INPUT_DATASET);
  return info.dataFrameSpec.columns;
}

export default function SaveToResultsEditor({
  config,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const inputColumns = useInputDataFrameColumns();

  const schemaId = (config.schemaId as string) ?? null;
  const columnMapping = useMemo(
    () => (config.columnMapping as unknown as Array<DataFrameColumnToFieldMapping>) ?? [],
    [config.columnMapping]
  );
  const targetSpecificMapping =
    (config.targetSpecificMapping as unknown as TargetSpecificMappingState) ?? null;

  const queryResult = useSkippableBenchlingQuery<GetResultSchemaQuery, GetResultSchemaQueryVariables>(
    GET_RESULT_SCHEMA_QUERY,
    {variables: {schemaId}, skip: schemaId == null}
  );

  const resultSchema = useMemo<ResultSchema | null>(() => {
    if (queryResult.type !== QueryResultType.SUCCESS) {
      return null;
    }
    const gqlSchema = queryResult.data.resultSchema;
    if (gqlSchema == null || isInaccessible(gqlSchema)) {
      return null;
    }
    return graphQLToREST('ResultSchema', gqlSchema);
  }, [queryResult]);

  const isLoading = schemaId != null && queryResult.type === QueryResultType.PENDING;

  const handleSchemaChange = useCallback(
    (newSchema: ResultSchema | null) => {
      const defaultMappings =
        newSchema == null
          ? []
          : updateResultSchema({resultSchema: null, mappings: []}, newSchema, inputColumns).mappings;

      let updated = setValueInConfig(config, ['schemaId'], newSchema?.id ?? null);
      updated = setValueInConfig(updated, ['columnMapping'], defaultMappings as unknown as JSONValue);
      updated = setValueInConfig(updated, ['targetSpecificMapping'], null);
      onChangeConfig(updated, ['schemaId'], newSchema?.id ?? null);
    },
    [config, onChangeConfig, inputColumns]
  );

  const handleMappingsChange = useCallback(
    (newMappings: Array<DataFrameColumnToFieldMapping>) => {
      const updated = setValueInConfig(config, ['columnMapping'], newMappings as unknown as JSONValue);
      onChangeConfig(updated, ['columnMapping'], newMappings as unknown as JSONValue);
    },
    [config, onChangeConfig]
  );

  const handleTargetSpecificMappingChange = useCallback(
    (newState: TargetSpecificMappingState | null) => {
      const updated = setValueInConfig(config, ['targetSpecificMapping'], newState as unknown as JSONValue);
      onChangeConfig(updated, ['targetSpecificMapping'], newState as unknown as JSONValue);
    },
    [config, onChangeConfig]
  );

  if (isLoading) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <SaveToResultsStepper
      resultSchema={resultSchema}
      inputDataFrameSpec={{name: SaveToResultsStepDataName.INPUT_DATASET, columns: inputColumns}}
      columnMapping={columnMapping}
      targetSpecificMapping={targetSpecificMapping}
      onSchemaChange={handleSchemaChange}
      onMappingsChange={handleMappingsChange}
      onTargetSpecificMappingChange={handleTargetSpecificMappingChange}
      disabled={disabled}
    />
  );
}
